import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { getUTMs } from "../../hooks/useUTMs";
import ConsentCheckboxes, { honeypotValue } from "./ConsentCheckboxes";
import {
  BookOpen,
  Download,
  Star,
  Shield,
  Zap,
  CheckCircle2,
  Loader2,
  User,
  Phone,
  Mail,
  ArrowRight,
  ArrowLeft,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import CTAButton from "./CTAButton";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const EBOOK_PDF_URL = "/downloads/O_Codigo_da_Riqueza.pdf";

const benefits = [
  { icon: <Zap size={14}/>, text: "Estratégia de alavancagem patrimonial passo a passo" },
  { icon: <Shield size={14}/>, text: "Como usar consórcio para multiplicar seu patrimônio" },
  { icon: <Star size={14}/>, text: "Erros que 90% dos investidores cometem e como evitá-los" },
  { icon: <BookOpen size={14}/>, text: "Casos reais de clientes que saíram do zero ao primeiro imóvel" },
];

const questions = [
  {
    id: "q1",
    stepTitle: "Perfil Financeiro",
    subtitle: "Relação com o dinheiro",
    question: "Como você resume a sua relação com o dinheiro hoje?",
    options: [
      {
        key: "A",
        label: "Meu dinheiro trabalha para construir meu patrimônio.",
        sub: "Foco em multiplicação, investimentos e visão de longo prazo."
      },
      {
        key: "B",
        label: "Meu dinheiro só serve para pagar os boletos do mês.",
        sub: "Foco na rotina, contas recorrentes e dificuldade em acumular."
      }
    ]
  },
  {
    id: "q2",
    stepTitle: "Principal Desafio",
    subtitle: "Diagnóstico de obstáculos",
    question: "Na sua opinião, o que mais impede o seu crescimento financeiro hoje?",
    options: [
      {
        key: "A",
        label: "A falta de planejamento e clareza estratégica.",
        sub: "Não ter uma rota clara ou estratégia definida para alavancar."
      },
      {
        key: "B",
        label: "Os juros abusivos ao buscar crédito ou financiamento bancário.",
        sub: "Custo alto dos financiamentos tradicionais que corrói o patrimônio."
      },
      {
        key: "C",
        label: "O medo de riscos desnecessários e promessas milagrosas.",
        sub: "Insegurança com consultorias duvidosas ou perda de capital."
      }
    ]
  },
  {
    id: "q3",
    stepTitle: "Perfil de Investidor",
    subtitle: "Maturidade patrimonial",
    question: "Qual é a sua experiência atual com investimentos e patrimônio?",
    options: [
      {
        key: "A",
        label: "Investidor Ativo",
        sub: "Já invisto (imóveis, agronegócio, ações ou renda fixa) e busco alavancagem inteligente para expandir."
      },
      {
        key: "B",
        label: "Investidor Tradicional / Conservador",
        sub: "Deixo recursos em poupança, CDI ou terrenos, mas sei que poderiam render muito mais."
      },
      {
        key: "C",
        label: "Em fase de estruturação",
        sub: "Ainda não sou investidor ativo, mas quero começar a construir meu primeiro patrimônio sólido."
      }
    ]
  },
  {
    id: "q4",
    stepTitle: "Objetivo Central",
    subtitle: "Airbnb, Aposentadoria ou Alavancagem",
    question: "Qual é o seu objetivo principal no mercado imobiliário hoje?",
    options: [
      {
        key: "A",
        label: "Geração de Renda com Airbnb / Locação por Temporada",
        sub: "Alta rentabilidade mensal e giro rápido de diárias com imóveis estratégicos."
      },
      {
        key: "B",
        label: "Aposentadoria Imobiliária Vitalícia",
        sub: "Construir carteira de imóveis gerando aluguéis mensais previsíveis e segurança familiar."
      },
      {
        key: "C",
        label: "Alavancagem Patrimonial pura com Consórcio",
        sub: "Comprar cotas estratégicas para lucrar na venda de contempladas ou valorização do bem."
      },
      {
        key: "D",
        label: "Imóvel Próprio / Campo / Expansão Familiar",
        sub: "Aquisição ou reforma de residência, sítio, sede rural ou expansão para minha família."
      }
    ]
  },
  {
    id: "q5",
    stepTitle: "Aporte e Alavancagem",
    subtitle: "Capacidade de investimento mensal",
    question: "Pensando em alavancar esse patrimônio sem juros bancários, qual faixa de aporte mensal cabe no seu orçamento?",
    options: [
      {
        key: "A",
        label: "Até R$ 1.500 / mês",
        sub: "Construção progressiva e disciplinada de patrimônio de médio e longo prazo."
      },
      {
        key: "B",
        label: "De R$ 1.500 a R$ 3.500 / mês",
        sub: "Ideal para cartas de R$ 200 mil a R$ 500 mil com estratégia de contemplação acelerada."
      },
      {
        key: "C",
        label: "Acima de R$ 3.500 / mês ou Lances Altos",
        sub: "Foco em alavancagem de alta escala, múltiplos Airbnbs, imóveis comerciais ou agro."
      },
      {
        key: "D",
        label: "Desejo uma consultoria personalizada",
        sub: "Quero uma análise técnica individual para desenhar o plano ideal para minha renda."
      }
    ]
  }
];

export default function EbookSection() {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    q1: "",
    q2: "",
    q3: "",
    q4: "",
    q5: ""
  });
  const [f, setF] = useState({ name: "", phone: "", email: "", investment_range: "100k-300k" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lgpdAccepted, setLgpdAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);

  const totalSteps = questions.length + 1; // 5 perguntas + etapa de cadastro

  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, "");
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const setField = (key) => (e) => {
    const val = key === "phone" ? formatPhone(e.target.value) : e.target.value;
    setF({ ...f, [key]: val });
  };

  const handleSelectOption = (questionKey, optionKey, optionLabel) => {
    setAnswers((prev) => ({
      ...prev,
      [questionKey]: `${optionKey}) ${optionLabel}`
    }));
    setTimeout(() => {
      setStep((prev) => prev + 1);
    }, 240);
  };

  const getProfileSummary = () => {
    const parts = [];
    if (answers.q3?.includes("Investidor Ativo")) parts.push("Investidor Ativo");
    else if (answers.q3?.includes("Tradicional")) parts.push("Investidor Conservador");
    else if (answers.q3?.includes("estruturação")) parts.push("Iniciante em Estruturação");

    if (answers.q4?.includes("Airbnb")) parts.push("Foco: AIRBNB / Temporada");
    else if (answers.q4?.includes("Aposentadoria")) parts.push("Foco: APOSENTADORIA IMOBILIÁRIA");
    else if (answers.q4?.includes("Alavancagem")) parts.push("Foco: ALAVANCAGEM PATRIMONIAL");
    else if (answers.q4?.includes("Próprio")) parts.push("Foco: IMÓVEL PRÓPRIO / AGRO");

    if (answers.q5?.includes("Até R$ 1.500")) parts.push("Aporte: Até 1.5k/mês");
    else if (answers.q5?.includes("1.500 a R$ 3.500")) parts.push("Aporte: 1.5k-3.5k/mês");
    else if (answers.q5?.includes("Acima de R$ 3.500")) parts.push("Aporte: 3.5k+/mês");
    else if (answers.q5?.includes("consultoria")) parts.push("Aporte: A Definir");

    return parts.length > 0 ? parts.join(" · ") : "Perfil Geral";
  };

  const triggerDownload = () => {
    const link = document.createElement("a");
    link.href = EBOOK_PDF_URL;
    link.download = "O_Codigo_da_Riqueza_com_Consorcio.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!f.name.trim()) {
      toast.error("Por favor, preencha seu nome completo.");
      return;
    }

    const rawDigits = f.phone.replace(/\D/g, "");
    if (rawDigits.length < 10) {
      toast.error("Por favor, insira um WhatsApp válido com DDD.");
      return;
    }

    if (!f.email.trim() || !f.email.includes("@")) {
      toast.error("Por favor, insira um e-mail válido.");
      return;
    }

    if (!lgpdAccepted) {
      toast.error("Marque a autorização de contato para liberar o download.");
      return;
    }

    setLoading(true);

    const profileSummary = getProfileSummary();

    const surveyMessage = [
      "📥 DOWNLOAD EBOOK: O CÓDIGO DA RIQUEZA",
      "----------------------------------------",
      `🎯 PERFIL IDENTIFICADO: ${profileSummary}`,
      "----------------------------------------",
      `1. Relação com o dinheiro: ${answers.q1 || "Não respondida"}`,
      `2. O que mais impede o crescimento: ${answers.q2 || "Não respondida"}`,
      `3. Perfil de Investidor: ${answers.q3 || "Não respondida"}`,
      `4. Objetivo Imobiliário: ${answers.q4 || "Não respondida"}`,
      `5. Faixa de Aporte / Alavancagem: ${answers.q5 || "Não respondida"}`,
      "----------------------------------------",
      "LGPD: Consentimento de uso de dados autorizado pelo usuário.",
      "Origem: Formulário da Seção Ebook no Site"
    ].join("\n");

    let invRange = f.investment_range;
    if (answers.q5?.includes("Até R$ 1.500")) invRange = "Até 100 mil";
    else if (answers.q5?.includes("1.500 a R$ 3.500")) invRange = "100k-300k";
    else if (answers.q5?.includes("Acima de R$ 3.500")) invRange = "300k-500k+";

    const payload = {
      name: f.name.trim(),
      phone: f.phone.trim(),
      email: f.email.trim(),
      investment_range: invRange,
      message: surveyMessage,
      source: "ebook",
      lgpd_accepted: true,
      marketing_consent: marketingAccepted,
      utm: getUTMs(),
      website: honeypotValue(e)
    };

    try {
      await axios.post(`${API}/leads`, payload);
      setSubmitted(true);
      toast.success("Download liberado! O arquivo está sendo baixado.");
      triggerDownload();

      if (window.gtag) {
        window.gtag("event", "Lead", { event_category: "ebook", event_label: "download_gratuito_com_pesquisa" });
      }
      if (window.fbq) {
        window.fbq("track", "Lead", { value: 0, currency: "BRL" });
        window.fbq("track", "ViewContent", { content_name: "Ebook O Codigo da Riqueza Gratuito" });
      }
    } catch (err) {
      console.error(err);
      let msg = "Erro ao processar solicitação. Tente novamente.";
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") msg = detail;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = step <= questions.length ? questions[step - 1] : null;

  return (
    <section id="ebook" className="py-24 lg:py-32 relative overflow-hidden" data-testid="ebook-section">
      {/* Fundo decorativo */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-border"/>
        <div className="absolute bottom-0 left-0 w-full h-px bg-border"/>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-3xl"/>
      </div>

      <div className="container relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">

          {/* Capa do Ebook & Benefícios (Coluna da Esquerda) */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-5 flex flex-col items-center lg:items-end text-center lg:text-right"
          >
            <div className="relative mb-8">
              {/* Sombra dourada decorativa */}
              <div className="absolute -inset-4 bg-primary/10 rounded-lg blur-2xl"/>
              <div className="relative border border-primary/30 rounded-lg overflow-hidden shadow-gold w-60 md:w-72">
                <img
                  src="/images/ebook-cover.png"
                  alt="Capa do ebook O Código da Riqueza com Consórcio - Juliano Wagner"
                  className="block w-full h-auto"
                  loading="lazy"
                />
              </div>
              {/* Badge 100% Gratuito */}
              <div
                className="absolute -bottom-4 -right-4 w-22 h-22 rounded-full flex flex-col items-center justify-center shadow-gold border border-primary/40 text-center px-1"
                style={{ background: "var(--gold-gradient)" }}
              >
                <span className="text-[9px] font-mono text-background font-extrabold uppercase tracking-wider">100%</span>
                <span className="text-lg font-display font-black text-background leading-none">GRÁTIS</span>
                <span className="text-[8px] font-mono text-background uppercase tracking-widest mt-0.5">DOWNLOAD</span>
              </div>
            </div>

            <div className="w-full max-w-sm">
              <ul className="space-y-2.5 text-left">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-center gap-3 text-xs sm:text-sm text-muted-foreground">
                    <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                      {b.icon}
                    </span>
                    {b.text}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Widget Interativo de Perguntas + Download (Coluna da Direita) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="eyebrow mb-3">
              <span className="kicker-line"/>Exemplar digital gratuito · Diagnóstico Rápido
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-light mb-3">
              O guia que transforma<br/>
              <span className="italic text-gold-gradient font-normal">consórcio em patrimônio</span>.
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-xl">
              Responda a 5 perguntas rápidas de diagnóstico para liberarmos o seu exemplar digital gratuito e receber o direcionamento sob medida para o seu objetivo patrimonial.
            </p>

            {/* Container do Formulário / Quiz */}
            <div className="bg-card-gradient border border-primary/30 rounded-2xl p-6 sm:p-8 shadow-gold relative overflow-hidden">
              
              {/* Barra de Progresso */}
              {!submitted && (
                <div className="mb-6">
                  <div className="flex items-center justify-between text-[11px] font-mono text-muted-foreground mb-1.5">
                    <span className="text-primary uppercase tracking-wider font-semibold">
                      {step <= questions.length
                        ? `Etapa ${step} de ${totalSteps} · ${currentQuestion?.stepTitle}`
                        : `Etapa ${totalSteps} de ${totalSteps} · Liberação do Download`}
                    </span>
                    <span>{Math.round((step / totalSteps) * 100)}%</span>
                  </div>
                  <div className="w-full bg-border/40 h-1.5 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-300 ease-out rounded-full"
                      style={{
                        width: `${(step / totalSteps) * 100}%`,
                        background: "var(--gold-gradient)"
                      }}
                    />
                  </div>
                </div>
              )}

              <AnimatePresence mode="wait">
                {submitted ? (
                  /* Tela de Sucesso */
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-4"
                  >
                    <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-primary/15 ring-2 ring-primary/40 text-primary">
                      <CheckCircle2 size={34} />
                    </div>
                    <span className="inline-block text-[11px] uppercase tracking-widest text-primary font-mono mb-1">
                      Download Liberado
                    </span>
                    <h3 className="font-display text-2xl sm:text-3xl font-light mb-2">
                      Seu exemplar está pronto!
                    </h3>
                    <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6 leading-relaxed">
                      O download do PDF começou automaticamente. Também enviamos uma cópia direta com as diretrizes para <strong className="text-foreground">{f.email}</strong>.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                      <button
                        type="button"
                        onClick={triggerDownload}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-medium text-sm transition-all shadow-gold hover:translate-y-[-1px] cursor-pointer"
                        style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
                      >
                        <Download size={16} /> Baixar PDF Novamente
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setStep(1);
                          setSubmitted(false);
                          setAnswers({ q1: "", q2: "", q3: "", q4: "", q5: "" });
                        }}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-medium text-sm border border-border hover:bg-white/5 transition-colors text-muted-foreground hover:text-foreground cursor-pointer"
                      >
                        Novo Diagnóstico
                      </button>
                    </div>
                  </motion.div>
                ) : step <= questions.length ? (
                  /* Perguntas Dinâmicas (1 a 5) */
                  <motion.div
                    key={`step-${step}`}
                    initial={{ opacity: 0, x: 15 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -15 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="mb-2">
                      <span className="text-[10px] uppercase tracking-widest font-mono text-primary/80">
                        {currentQuestion.subtitle}
                      </span>
                    </div>

                    <h3 className="text-xl sm:text-2xl font-display font-light mb-5 leading-snug">
                      {currentQuestion.question}
                    </h3>

                    <div className="space-y-3 mb-6">
                      {currentQuestion.options.map((opt) => {
                        const answerKey = `q${step}`;
                        const isSelected = answers[answerKey]?.startsWith(opt.key);
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => handleSelectOption(currentQuestion.id, opt.key, opt.label)}
                            className={`w-full text-left p-4 rounded-xl border transition-all duration-200 flex items-start gap-3.5 cursor-pointer ${
                              isSelected
                                ? "border-primary bg-primary/10 ring-1 ring-primary/40 shadow-gold"
                                : "border-border bg-background/50 hover:border-primary/50 hover:bg-background/80"
                            }`}
                          >
                            <div
                              className={`w-7 h-7 rounded-lg flex items-center justify-center font-mono font-bold text-xs shrink-0 ${
                                isSelected
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-white/5 text-muted-foreground border border-border"
                              }`}
                            >
                              {opt.key}
                            </div>
                            <div>
                              <div className="font-medium text-foreground text-sm sm:text-base">
                                {opt.label}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {opt.sub}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      {step > 1 ? (
                        <button
                          type="button"
                          onClick={() => setStep((prev) => prev - 1)}
                          className="inline-flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer"
                        >
                          <ArrowLeft size={13} /> Voltar à pergunta anterior
                        </button>
                      ) : (
                        <span />
                      )}
                      <span className="text-xs">Toque em uma opção para avançar →</span>
                    </div>
                  </motion.div>
                ) : (
                  /* Etapa Final: Formulário de Liberação */
                  <motion.div
                    key="step-final"
                    initial={{ opacity: 0, x: 15 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -15 }}
                    transition={{ duration: 0.25 }}
                  >
                    {/* Preview do Perfil Identificado */}
                    <div className="bg-primary/10 border border-primary/30 rounded-xl p-3.5 mb-4 flex items-center gap-2.5">
                      <Sparkles size={16} className="text-primary shrink-0" />
                      <div className="text-xs text-foreground">
                        <span className="text-muted-foreground">Perfil mapeado: </span>
                        <strong className="text-primary font-medium">{getProfileSummary()}</strong>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h3 className="text-xl sm:text-2xl font-display font-light mb-1">
                        Para onde enviamos o seu <span className="italic text-gold-gradient font-normal">exemplar gratuito</span>?
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Preencha seus dados abaixo para liberar o download imediato do PDF.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-3.5">
                      <div>
                        <label htmlFor="ebook-field-1" className="block text-sm text-muted-foreground font-medium mb-1.5">
                          Nome completo <span className="text-primary">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                            <User size={16} />
                          </div>
                          <input
                            id="ebook-field-1"
                            type="text"
                            required
                            maxLength={100}
                            value={f.name}
                            onChange={setField("name")}
                            placeholder="Seu nome"
                            className="w-full pl-9 pr-3.5 py-2.5 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/70 text-base font-body"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-3.5">
                        <div>
                          <label htmlFor="ebook-field-2" className="block text-sm text-muted-foreground font-medium mb-1.5">
                            WhatsApp <span className="text-primary">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                              <Phone size={16} />
                            </div>
                            <input
                              id="ebook-field-2"
                              type="tel"
                              required
                              maxLength={16}
                              value={f.phone}
                              onChange={setField("phone")}
                              placeholder="(00) 00000-0000"
                              className="w-full pl-9 pr-3.5 py-2.5 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/70 text-base font-body"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="ebook-field-3" className="block text-sm text-muted-foreground font-medium mb-1.5">
                            E-mail <span className="text-primary">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                              <Mail size={16} />
                            </div>
                            <input
                              id="ebook-field-3"
                              type="email"
                              required
                              maxLength={120}
                              value={f.email}
                              onChange={setField("email")}
                              placeholder="seu@email.com"
                              className="w-full pl-9 pr-3.5 py-2.5 rounded-lg bg-background border border-input text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/70 text-base font-body"
                            />
                          </div>
                        </div>
                      </div>

                      <ConsentCheckboxes
                        idPrefix="ebook"
                        contactText="Autorizo o contato por WhatsApp e e-mail para receber o material solicitado."
                        contact={lgpdAccepted}
                        onContactChange={setLgpdAccepted}
                        marketing={marketingAccepted}
                        onMarketingChange={setMarketingAccepted}
                      />

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3.5 mt-2 rounded-full font-medium text-sm sm:text-base transition-all shadow-gold hover:translate-y-[-1px] disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer"
                        style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="animate-spin" size={18} /> Liberando exemplar...
                          </>
                        ) : (
                          <>
                            Liberar Meu Ebook Gratuito (PDF) <Download size={17} />
                          </>
                        )}
                      </button>

                      <div className="flex items-center justify-between pt-2 border-t border-border/40 text-xs text-muted-foreground">
                        <button
                          type="button"
                          onClick={() => setStep(questions.length)}
                          className="inline-flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer"
                        >
                          <ArrowLeft size={13} /> Voltar à pergunta 5
                        </button>
                        <span>Seus dados ficam 100% seguros</span>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
        
        <div className="mt-20 border-t border-border pt-12">
          <p className="text-center text-muted-foreground mb-4">Prefere uma análise personalizada do seu caso?</p>
          <CTAButton centered className="!mt-0" />
        </div>
      </div>
    </section>
  );
}
